import { Button, Empty, Modal, Timeline } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { getAudit } from '../../services/objectiveServices';
import { auditTrailData } from '../../types';
import Loading from '../loading';
import { Statuses, statusesLabel } from '../../helper';
import NotificationAlert from '../../components/notification/index';

interface Props {
  open: boolean;
  setOpen(open: boolean): void;
  id: string;
}

enum OperationType {
  createObjective = 'CREATE_OBJECTIVE',
  updateObjectiveStatus = 'UPDATE_OBJECTIVE_STATUS',
  updateObjectiveTargetDate = 'UPDATE_OBJECTIVE_TARGET_DATE',
}

const AuditTrail = ({ open, setOpen, id }: Props) => {
  const myRef = useRef<any>();
  const [data, setData] = useState<auditTrailData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAudit = async (id: string) => {
    setLoading(true);
    const { error, data } = await getAudit(id);
    if (error) return myRef.current?.call('error', error?.message);
    setData(data);
    setLoading(false);
  };

  const formattedDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    // Add 5 hours
    date.setHours(date.getHours() + 5);
    // Add 30 minutes
    date.setMinutes(date.getMinutes() + 30);

    return date.toLocaleString('en-US', {
      month: 'long', // numeric, 2-digit, long, short, narrow
      day: '2-digit', // numeric, 2-digit
      year: 'numeric', // numeric, 2-digit
      hour: 'numeric', // numeric, 2-digit
      minute: 'numeric', // numeric, 2-digit
      hour12: true, // Use 12-hour time
    });
  };

  useEffect(() => {
    if (open) fetchAudit(id);
  }, [open]);

  const filterAuditTrail = (data: auditTrailData[]) => {
    const auditTrail = [];
    let index = 0;
    const filteredData = data.filter(
      (item) =>
        OperationType.createObjective === item.operationType ||
        OperationType.updateObjectiveStatus === item.operationType ||
        OperationType.updateObjectiveTargetDate === item.operationType
    );

    if (filteredData.length === 0) return [];

    auditTrail.push(
      ...filteredData.filter(
        (item) => item.operationType === OperationType.createObjective
      )
    );
    auditTrail.push(
      ...filteredData.filter((item, idx: number) => {
        if (
          item.operationType === OperationType.updateObjectiveStatus &&
          item.originalData === Statuses.Draft &&
          item.newData === Statuses.NotStarted
        ) {
          index = idx;
          return item;
        }
      })
    );

    if (filteredData[index + 1]) {
      if (filteredData[index + 1].changedAt === filteredData[index].changedAt) {
        auditTrail.push(
          ...filteredData.slice(index + 2).map((item) => {
            if (
              OperationType.updateObjectiveStatus === item.operationType ||
              OperationType.updateObjectiveTargetDate === item.operationType
            ) {
              return item;
            }
          })
        );
      } else {
        auditTrail.push(
          ...filteredData.slice(index + 1).map((item) => {
            if (
              OperationType.updateObjectiveStatus === item.operationType ||
              OperationType.updateObjectiveTargetDate === item.operationType
            ) {
              return item;
            }
          })
        );
      }
    }

    return (
      <Timeline className="mt-[30px]">
        {auditTrail.length > 0 &&
          auditTrail
            .map((item, idx: number) => {
              if (!item) return null;
              return (
                <Timeline.Item key={idx}>
                  <Message
                    operationType={item.operationType}
                    changedAt={formattedDateTime(item.changedAt)}
                    changedByUser={item.changedByUser.name}
                    originalData={item.originalData}
                    newData={item.newData}
                    reason={item.reason}
                  />
                </Timeline.Item>
              );
            })
            .reverse()}
      </Timeline>
    );
  };

  return (
    <>
      <Modal
        title="Audit Trail"
        centered
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <div>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </div>
        }
      >
        {loading ? (
          <Loading />
        ) : !loading && data.length === 0 ? (
          <Empty />
        ) : (
          <div
            className="overflow-auto h-auto"
            style={{ height: 'auto', maxHeight: 'calc(100vh - 300px)' }}
          >
            {filterAuditTrail(data)}
          </div>
        )}
      </Modal>
      <NotificationAlert ref={myRef} />
    </>
  );
};

export default AuditTrail;

interface MessageProps {
  operationType: string;
  changedAt: string;
  originalData: string;
  newData: string;
  reason: string;
  changedByUser: string;
}

const Message = ({
  operationType,
  changedAt,
  originalData,
  newData,
  reason,
  changedByUser,
}: MessageProps) => {
  const formattedDate = (dateString: string): string => {
    const date = new Date(dateString);
    // Add 5 hours
    date.setHours(date.getHours() + 5);
    // Add 30 minutes
    date.setMinutes(date.getMinutes() + 30);

    return date.toLocaleString('en-US', {
      month: 'long', // numeric, 2-digit, long, short, narrow
      day: '2-digit', // numeric, 2-digit
      year: 'numeric', // numeric, 2-digit
    });
  };
  return (
    <div>
      {operationType === OperationType.createObjective ? (
        <>
          <div>
            <b>Objective Created </b>by<b> {changedByUser}</b>{' '}
          </div>
          <div className="text-[#6C6C6C] mt-2">
            <div className="text-[12px]">{changedAt}</div>
          </div>
        </>
      ) : (
        <>
          <div>
            {Statuses.Draft === originalData &&
            Statuses.NotStarted === newData ? (
              <>
                <b>Objective Activated </b>by<b> {changedByUser}</b>
              </>
            ) : (
              <>
                <b>{changedByUser}</b> updated{' '}
                {operationType === OperationType.updateObjectiveStatus ? (
                  <>
                    the <b>Status</b>
                  </>
                ) : operationType ===
                  OperationType.updateObjectiveTargetDate ? (
                  <>
                    the <b>Target Completion Date</b>
                  </>
                ) : (
                  ''
                )}
              </>
            )}
          </div>
          <div className="text-[#6C6C6C] mt-2">
            <div className="text-[12px]">{changedAt}</div>
            <div className="flex gap-x-3">
              <div>
                {operationType === OperationType.updateObjectiveStatus
                  ? statusesLabel[originalData]
                  : operationType === OperationType.updateObjectiveTargetDate
                    ? formattedDate(originalData)
                    : ''}
              </div>
              <div>
                <ArrowRightOutlined />
              </div>
              <div>
                {operationType === OperationType.updateObjectiveStatus
                  ? statusesLabel[newData]
                  : operationType === OperationType.updateObjectiveTargetDate
                    ? formattedDate(newData)
                    : ''}
              </div>
            </div>
            {Statuses.Draft === originalData &&
            Statuses.NotStarted === newData ? (
              ''
            ) : (
              <div className="text-[12px]">{reason}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
